import {
    PtiAssetType,
    PtiItemType,
    PreTripInspectionItemResponseDto,
    PreTripInspectionResponseDto,
} from "../../api/ptiApi";
import { PTI_POSITION_KEYS } from "./ptiConstants";

export interface PtiPositionDefinition {
    positionKey: string;
    itemType: PtiItemType;
}

const VEHICLE_POSITIONS: PtiPositionDefinition[] = [
    { positionKey: PTI_POSITION_KEYS.Front, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.Rear, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.DriverSide, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.PassengerSide, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.Dashboard, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.Coolant, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.Oil, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.FifthWheel, itemType: "BodyPhoto" },
    { positionKey: "steer_left", itemType: "Tire" },
    { positionKey: "steer_right", itemType: "Tire" },
    { positionKey: "drive_1_left_outer", itemType: "Tire" },
    { positionKey: "drive_1_left_inner", itemType: "Tire" },
    { positionKey: "drive_1_right_inner", itemType: "Tire" },
    { positionKey: "drive_1_right_outer", itemType: "Tire" },
    { positionKey: "drive_2_left_outer", itemType: "Tire" },
    { positionKey: "drive_2_left_inner", itemType: "Tire" },
    { positionKey: "drive_2_right_inner", itemType: "Tire" },
    { positionKey: "drive_2_right_outer", itemType: "Tire" },
];

/** Historical tire keys still shown when present on old inspections. */
const VEHICLE_LEGACY_TIRE_POSITIONS: PtiPositionDefinition[] = [
    { positionKey: "drive_1_left", itemType: "Tire" },
    { positionKey: "drive_1_right", itemType: "Tire" },
    { positionKey: "drive_2_left", itemType: "Tire" },
    { positionKey: "drive_2_right", itemType: "Tire" },
];

const TRAILER_POSITIONS: PtiPositionDefinition[] = [
    { positionKey: PTI_POSITION_KEYS.Front, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.Rear, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.DriverSide, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.PassengerSide, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.Doors, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.LeftInside, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.RightInside, itemType: "BodyPhoto" },
    { positionKey: PTI_POSITION_KEYS.InsideFloor, itemType: "BodyPhoto" },
    { positionKey: "axle_1_left_outer", itemType: "Tire" },
    { positionKey: "axle_1_left_inner", itemType: "Tire" },
    { positionKey: "axle_1_right_inner", itemType: "Tire" },
    { positionKey: "axle_1_right_outer", itemType: "Tire" },
    { positionKey: "axle_2_left_outer", itemType: "Tire" },
    { positionKey: "axle_2_left_inner", itemType: "Tire" },
    { positionKey: "axle_2_right_inner", itemType: "Tire" },
    { positionKey: "axle_2_right_outer", itemType: "Tire" },
];

/** Historical trailer tire keys still shown when present on old inspections. */
const TRAILER_LEGACY_TIRE_POSITIONS: PtiPositionDefinition[] = [
    { positionKey: "axle_1_left", itemType: "Tire" },
    { positionKey: "axle_1_right", itemType: "Tire" },
    { positionKey: "axle_2_left", itemType: "Tire" },
    { positionKey: "axle_2_right", itemType: "Tire" },
];

export const getPtiPositionsForAssetType = (assetType: PtiAssetType): PtiPositionDefinition[] =>
    assetType === "Vehicle" ? VEHICLE_POSITIONS : TRAILER_POSITIONS;

/** Merge API items with the full position set so gallery/diagram show missing photos too. */
export function mergePtiReviewItems(
    inspection: PreTripInspectionResponseDto
): PreTripInspectionItemResponseDto[] {
    const positions = getPtiPositionsForAssetType(inspection.assetType);
    const knownKeys = new Set(
        positions.map((p) => `${p.itemType}:${p.positionKey}`)
    );

    const merged = positions.map((position, index) => {
        const existing = inspection.items.find(
            (item) =>
                item.positionKey === position.positionKey &&
                item.itemType === position.itemType
        );

        if (existing) {
            return existing;
        }

        return {
            id: -(index + 1),
            assetType: inspection.assetType,
            itemType: position.itemType,
            positionKey: position.positionKey,
        };
    });

    // Keep leftover historical items (e.g. old dual tire keys) visible.
    const extras = inspection.items.filter(
        (item) => !knownKeys.has(`${item.itemType}:${item.positionKey}`)
    );

    return [...merged, ...extras];
}

export function isExteriorPosition(itemType: PtiItemType): boolean {
    return itemType === "BodyPhoto";
}

export function isTirePosition(itemType: PtiItemType): boolean {
    return itemType === "Tire";
}

export const VEHICLE_LEGACY_TIRE_KEYS = new Set(
    VEHICLE_LEGACY_TIRE_POSITIONS.map((p) => p.positionKey)
);

export const TRAILER_LEGACY_TIRE_KEYS = new Set(
    TRAILER_LEGACY_TIRE_POSITIONS.map((p) => p.positionKey)
);
